/* Newsletter Popup – Custom Web Component
 * Smart trigger logic: timer / exit-intent / scroll-depth based on template + device
 * Uses trapFocus / removeTrapFocus from global.js (loaded before this file)
 *
 * Trigger matrix:
 *   Homepage  · Desktop · First visit  → timer (delay_home)
 *   Homepage  · Desktop · Returning    → exit intent
 *   Homepage  · Mobile                 → timer (delay_home) OR scroll threshold
 *   Other     · Desktop · Any visit    → timer (delay_other) + exit intent (first wins)
 *   Other     · Mobile                 → timer (delay_other) OR scroll threshold
 *   Cart / Checkout                    → never (excluded in Liquid)
 */

const STORAGE_KEY = 'vegenergy_newsletter';
const VISITED_KEY = 'vegenergy_visited';

class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.overlay    = this.querySelector('.newsletter-popup__overlay');
    this.card       = this.querySelector('.newsletter-popup__card');
    this.closeBtn   = this.querySelector('.newsletter-popup__close');
    this.declineBtn = this.querySelector('.newsletter-popup__decline');

    this._onKeyUp           = this._onKeyUp.bind(this);
    this._onOverlayClick    = this._onOverlayClick.bind(this);
    this._exitIntentHandler = null;

    this._scrollHandler     = null;
    this._timer             = null;
  }

  connectedCallback() {
    if (this.closeBtn)   this.closeBtn.addEventListener('click',  () => this.dismiss());
    if (this.declineBtn) this.declineBtn.addEventListener('click', () => this.dismiss());
    if (this.overlay)    this.overlay.addEventListener('click', this._onOverlayClick);

    // Save scroll + set flag when user clicks submit. Shopify's hCaptcha then runs
    // natively. If no challenge: Shopify posts and redirects to /?customer_posted=true
    // → Liquid sets data-success="true" → success popup shown (handled below).
    // If challenge needed: Shopify redirects to /challenge, user solves it, comes back
    // to / → we detect the vegenergy_subscribing flag and show the success popup.
    const form = document.getElementById('NewsletterPopupForm');
    if (form) {
      form.addEventListener('submit', () => {
        sessionStorage.setItem('vegenergy_scroll', window.scrollY);
        // Store timestamp so the flag expires after 10 minutes (stale if challenge was abandoned)
        sessionStorage.setItem('vegenergy_subscribing', Date.now().toString());
        this.classList.add('newsletter-popup--checking');
      });
    }

    // Case 1: No challenge – Shopify redirected to /?customer_posted=true
    // Liquid sets data-success="true" on the element.
    if (this.dataset.success === 'true') {
      sessionStorage.removeItem('vegenergy_subscribing');
      this._markSubscribed();
      this.open(true);
      // trapFocus focuses a DOM element which causes the browser to scroll to it.
      // Counteract by scrolling back to top after the focus has been applied.
      requestAnimationFrame(() => window.scrollTo(0, 0));
      return;
    }

    // Case 2: Challenge was shown – user solved it and Shopify redirected back to /
    // (return_path in the challenge URL). data-success is NOT set because Shopify doesn't
    // add customer_posted param after a challenge, but the subscription was processed.
    // We detect the flag we set before the redirect. Flag expires after 10 minutes to
    // avoid a false success popup if the user abandoned the challenge and returned later.
    const subscribingTs = sessionStorage.getItem('vegenergy_subscribing');
    if (subscribingTs && (Date.now() - parseInt(subscribingTs, 10)) < 10 * 60 * 1000) {
      sessionStorage.removeItem('vegenergy_subscribing');
      this._markSubscribed();
      this.open(true);
      requestAnimationFrame(() => window.scrollTo(0, 0));
      return;
    }
    // Clear stale flag if it expired
    if (subscribingTs) sessionStorage.removeItem('vegenergy_subscribing');

    if (!this._shouldShow()) return;

    // Record visit timestamp (used for returning-visitor detection)
    this._markVisited();

    // Wait for Shopify cookie consent banner to be dismissed before starting triggers
    this._startAfterConsent();
  }

  disconnectedCallback() {
    this._clearTriggers();
    document.removeEventListener('keyup', this._onKeyUp);
  }

  // ── Public ────────────────────────────────────────────────

  open(successState = false) {
    if (successState) this.classList.add('newsletter-popup--success');

    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    // Save scroll position before locking (needed for iOS Safari position:fixed fix)
    this._savedScrollY = window.scrollY;
    document.body.classList.add('overflow-hidden');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._savedScrollY}px`;
    document.body.style.width = '100%';
    document.addEventListener('keyup', this._onKeyUp);

    const firstFocusable = this.card.querySelector(
      'input:not([type="hidden"]), button, a, [tabindex]:not([tabindex="-1"])'
    );
    if (typeof trapFocus === 'function') {
      trapFocus(this.card, firstFocusable);
    } else if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  dismiss() {
    if (!this.hasAttribute('open')) return;

    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this._savedScrollY || 0);
    document.removeEventListener('keyup', this._onKeyUp);

    if (typeof removeTrapFocus === 'function') removeTrapFocus();

    const stored = this._getStored();
    if (!stored.subscribed) {
      this._setStored({ dismissed: Date.now() });
    }
  }

  // ── Trigger setup ─────────────────────────────────────────

  _startAfterConsent() {
    const privacy = window.Shopify?.customerPrivacy;

    // Determine whether the cookie banner is still pending
    const bannerPending = () => {
      // Method 1: Shopify Privacy API (Dawn / newer themes)
      if (privacy) {
        if (typeof privacy.shouldShowBanner === 'function') return privacy.shouldShowBanner();
        // Older API fallback: empty string = no answer yet
        if (typeof privacy.trackingConsent === 'function') return privacy.trackingConsent() === '';
      }
      // Method 2: check if Shopify's cookie banner element is present in the DOM
      return !!document.querySelector('.shopify-pc__banner__dialog, #shopify-pc-banner');
    };

    if (!bannerPending()) {
      this._setupTriggers();
      return;
    }

    // Consent not yet collected → wait for the banner to be acted upon
    const onConsent = () => {
      document.removeEventListener('visitorConsentCollected', onConsent);
      this._markVisited();
      this._setupTriggers();
    };
    document.addEventListener('visitorConsentCollected', onConsent);
  }

  _setupTriggers() {
    const isMobile      = window.matchMedia('(max-width: 749px)').matches;
    const template      = this.dataset.template || '';
    const isReturning   = this._isReturningVisitor();
    const delayHome     = parseInt(this.dataset.delayHome, 10) || 10;
    const delayOther    = parseInt(this.dataset.delayOther, 10) || 15;
    const scrollThresh  = parseInt(this.dataset.scrollThreshold, 10) || 50;

    if (template === 'index') {
      if (isMobile) {
        // Mobile homepage: timer OR scroll depth – whichever fires first
        this._setupTimer(delayHome);
        this._setupScrollTrigger(scrollThresh);
      } else if (isReturning) {
        // Returning desktop visitor on homepage: exit intent only
        this._setupExitIntent();
      } else {
        // First-time desktop visitor on homepage: timer
        this._setupTimer(delayHome);
      }
    } else {
      // Product pages + all other pages
      if (isMobile) {
        // Mobile: timer OR scroll depth – whichever fires first
        this._setupTimer(delayOther);
        this._setupScrollTrigger(scrollThresh);
      } else {
        // Desktop (returning or first-time): timer + exit intent – whichever fires first
        this._setupTimer(delayOther);
        this._setupExitIntent();
      }
    }
  }

  _setupTimer(seconds) {
    this._timer = setTimeout(() => this._trigger(), seconds * 1000);
  }

  _setupExitIntent() {
    this._exitIntentHandler = (e) => {
      // Fires when the cursor leaves the viewport through the top (towards browser chrome)
      if (e.clientY <= 2) this._trigger();
    };
    document.addEventListener('mouseleave', this._exitIntentHandler);
  }

  _setupScrollTrigger(thresholdPercent) {
    this._scrollHandler = () => {
      const scrollable = document.body.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = (window.scrollY / scrollable) * 100;
      if (pct >= thresholdPercent) this._trigger();
    };
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
  }

  _trigger() {
    this._clearTriggers();
    this.open();
  }

  _clearTriggers() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
    if (this._exitIntentHandler) {
      document.removeEventListener('mouseleave', this._exitIntentHandler);
      this._exitIntentHandler = null;
    }
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
      this._scrollHandler = null;
    }
  }

  // ── State helpers ─────────────────────────────────────────

  _shouldShow() {
    const stored = this._getStored();
    if (stored.subscribed) return false;
    if (stored.dismissed) {
      const days       = parseInt(this.dataset.showAfterDays, 10) || 7;
      const cooldownMs = days * 24 * 60 * 60 * 1000;
      if (Date.now() - stored.dismissed < cooldownMs) return false;
    }
    return true;
  }

  _isReturningVisitor() {
    try {
      const ts = localStorage.getItem(VISITED_KEY);
      // "Returning" = visited before AND last visit was more than 30 minutes ago
      return ts !== null && (Date.now() - parseInt(ts, 10)) > 30 * 60 * 1000;
    } catch { return false; }
  }

  _markVisited() {
    try {
      // Set only on first visit so the timestamp marks that initial visit
      if (!localStorage.getItem(VISITED_KEY)) {
        localStorage.setItem(VISITED_KEY, Date.now().toString());
      }
    } catch {}
  }

  _markSubscribed() {
    this._setStored({ subscribed: true });
  }

  _getStored() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
    catch { return {}; }
  }

  _setStored(data) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); }
    catch {}
  }

  _onKeyUp(event) {
    if (event.code && event.code.toUpperCase() === 'ESCAPE') this.dismiss();
  }

  _onOverlayClick(event) {
    if (event.target === this.overlay) this.dismiss();
  }

}


customElements.define('newsletter-popup', NewsletterPopup);
